import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

// 主题配置
const themes = {
  light: {
    name: 'light',
    colors: {
      primary: '#667eea',
      secondary: '#764ba2',
      background: '#ffffff',
      surface: '#f8fafc',
      text: {
        primary: '#1a202c',
        secondary: '#4a5568',
        muted: '#718096',
      },
      border: '#e2e8f0',
      shadow: 'rgba(0, 0, 0, 0.1)',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
  },
  dark: {
    name: 'dark',
    colors: {
      primary: '#667eea',
      secondary: '#764ba2',
      background: '#1a202c',
      surface: '#2d3748',
      text: {
        primary: '#f7fafc',
        secondary: '#e2e8f0',
        muted: '#a0aec0',
      },
      border: '#4a5568',
      shadow: 'rgba(0, 0, 0, 0.3)',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
  },
  classic: {
    name: 'classic',
    colors: {
      primary: '#8B4513',
      secondary: '#CD853F',
      background: '#faf5f0',
      surface: '#f5f0e8',
      text: {
        primary: '#2d1810',
        secondary: '#5d4037',
        muted: '#8d6e63',
      },
      border: '#d7ccc8',
      shadow: 'rgba(139, 69, 19, 0.1)',
      gradient: 'linear-gradient(135deg, #8B4513 0%, #CD853F 100%)',
    },
  },
};

// ThemeProvider 组件
export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme && themes[savedTheme] ? savedTheme : 'light';
  });

  const [fontSize, setFontSize] = useState(() => {
    const savedFontSize = localStorage.getItem('fontSize');
    return savedFontSize ? parseInt(savedFontSize) : 16;
  });

  const [animations, setAnimations] = useState(() => {
    const savedAnimations = localStorage.getItem('animations');
    return savedAnimations !== null ? JSON.parse(savedAnimations) : true;
  });

  // 切换主题
  const toggleTheme = () => {
    const themeNames = Object.keys(themes);
    const currentIndex = themeNames.indexOf(currentTheme);
    const nextIndex = (currentIndex + 1) % themeNames.length;
    const nextTheme = themeNames[nextIndex];
    setCurrentTheme(nextTheme);
  };

  // 设置特定主题
  const setTheme = (themeName) => {
    if (themes[themeName]) {
      setCurrentTheme(themeName);
    }
  };

  // 调整字体大小
  const adjustFontSize = (size) => {
    const newSize = Math.max(12, Math.min(24, size));
    setFontSize(newSize);
  };

  // 增加字体大小
  const increaseFontSize = () => {
    adjustFontSize(fontSize + 1);
  };

  // 减少字体大小
  const decreaseFontSize = () => {
    adjustFontSize(fontSize - 1);
  };

  // 重置字体大小
  const resetFontSize = () => {
    setFontSize(16);
  };

  // 切换动画
  const toggleAnimations = () => {
    setAnimations(!animations);
  };

  // 获取当前主题对象
  const theme = themes[currentTheme];

  // 应用主题到 CSS 变量
  useEffect(() => {
    const root = document.documentElement;
    const colors = theme.colors;

    // 设置 CSS 变量
    root.style.setProperty('--color-primary', colors.primary);
    root.style.setProperty('--color-secondary', colors.secondary);
    root.style.setProperty('--color-background', colors.background);
    root.style.setProperty('--color-surface', colors.surface);
    root.style.setProperty('--color-text-primary', colors.text.primary);
    root.style.setProperty('--color-text-secondary', colors.text.secondary);
    root.style.setProperty('--color-text-muted', colors.text.muted);
    root.style.setProperty('--color-border', colors.border);
    root.style.setProperty('--color-shadow', colors.shadow);
    root.style.setProperty('--gradient-primary', colors.gradient);

    // 设置字体大小
    root.style.setProperty('--font-size-base', `${fontSize}px`);

    // 设置动画
    root.style.setProperty('--animation-duration', animations ? '0.3s' : '0s');

    // 更新 body 背景色
    document.body.style.backgroundColor = colors.background;
    document.body.style.color = colors.text.primary;

    // 保存到 localStorage
    localStorage.setItem('theme', currentTheme);
    localStorage.setItem('fontSize', fontSize.toString());
    localStorage.setItem('animations', JSON.stringify(animations));
  }, [currentTheme, fontSize, animations, theme]);

  // 监听系统主题变化
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      // 只有在用户没有手动设置主题时才自动切换
      const hasManualTheme = localStorage.getItem('theme');
      if (!hasManualTheme) {
        setCurrentTheme(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // 获取主题相关的工具函数
  const getThemeColor = (colorPath) => {
    const paths = colorPath.split('.');
    let color = theme.colors;
    
    for (const path of paths) {
      color = color[path];
      if (!color) break;
    }
    
    return color || theme.colors.primary;
  };

  // 检查是否为深色主题
  const isDark = currentTheme === 'dark';

  // 检查是否为经典主题
  const isClassic = currentTheme === 'classic';

  // 获取对比色
  const getContrastColor = (backgroundColor) => {
    // 简单的对比度计算
    const hex = backgroundColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? '#000000' : '#ffffff';
  };

  const value = {
    // 当前状态
    currentTheme,
    theme,
    fontSize,
    animations,
    isDark,
    isClassic,
    
    // 主题操作
    toggleTheme,
    setTheme,
    
    // 字体大小操作
    adjustFontSize,
    increaseFontSize,
    decreaseFontSize,
    resetFontSize,
    
    // 动画操作
    toggleAnimations,
    
    // 工具函数
    getThemeColor,
    getContrastColor,
    
    // 可用主题
    availableThemes: Object.keys(themes),
    themes,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// 自定义 Hook
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;